import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, Rocket, Loader2, CheckCircle, Lock, 
  Send, Eye, AlertCircle, Lightbulb, TrendingUp, RefreshCw, Award
} from "lucide-react";

export const RapidSession3Page = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [formData, setFormData] = useState({
    decisions_shifted: "",
    execution_easier: "",
    remaining_friction: "",
    next_30_days: ""
  });

  useEffect(() => {
    fetchEnrollment();
  }, []);

  const fetchEnrollment = async () => {
    try {
      const response = await authFetch(`${API}/rapid/enrollment`, {  });
      if (response.ok) {
        const data = await response.json();
        setEnrollment(data);
        if (data.session_3?.pre_session_response) {
          try {
            const savedData = JSON.parse(data.session_3.pre_session_response);
            setFormData(prev => ({ ...prev, ...savedData }));
          } catch {
            // Response might be plain text from old format
          }
        }
      }
    } catch (_error) {
      console.error("Error fetching enrollment:", _error);
    } finally {
      setLoading(false);
    }
  };

  const isLocked = () => {
    if (!enrollment) return true;
    return !enrollment.session_2?.completed;
  };

  const isCompleted = () => {
    return enrollment?.session_3?.completed === true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await authFetch(`${API}/rapid/session/3/pre-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({ response: JSON.stringify(formData) })
      });

      if (response.ok) {
        toast.success("Pre-session reflection submitted successfully!");
        fetchEnrollment();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isLocked()) {
    return (
      <div className="min-h-screen bg-neutral-50" data-testid="session-3-locked">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Lock className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-neutral-900 mb-2">Session 3 is Locked</h1>
          <p className="text-neutral-600 mb-6">Please complete Session 2 first to unlock this session.</p>
          <Button onClick={() => navigate("/rapid/session/2")} className="bg-emerald-600 hover:bg-emerald-700">
            Go to Session 2
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="rapid-session-3">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/rapid")} className="text-neutral-600">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Dashboard
            </Button>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h1 className="text-xl font-semibold text-neutral-900">Session 3: Develop & Integrate</h1>
                {isCompleted() && <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>}
              </div>
              <p className="text-sm text-neutral-500">R.A.P.I.D.™ Business Recalibration</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {/* Page Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 mb-4">
            <Rocket className="w-8 h-8 text-orange-600" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">Develop & Integrate</h2>
          <p className="text-neutral-600 max-w-xl mx-auto">
            <strong>Objective:</strong> Translate recalibrated decisions into sustained execution momentum.
          </p>
        </div>

        {/* Section 1: Session Context */}
        <Card className="border-neutral-200">
          <CardHeader className="bg-slate-50 border-b">
            <div className="flex items-center space-x-2">
              <Eye className="w-5 h-5 text-slate-600" />
              <CardTitle className="text-lg">What We&apos;re Doing in This Session</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-neutral-700 mb-4">
              This session locks in decisions and converts clarity into consistent execution.
            </p>
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
              <p className="font-medium text-orange-800">
                The focus is momentum, not perfection.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Pre-Session Reflection Form */}
        <Card className="border-neutral-200">
          <CardHeader className="bg-orange-50 border-b border-orange-100">
            <CardTitle className="text-lg">Execution Momentum Check</CardTitle>
            <CardDescription className="text-orange-700 mt-1">
              Reflect on what has changed since Session 2.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-base font-medium">
                  1. What decisions have already shifted since Session 2?
                </Label>
                <Textarea
                  value={formData.decisions_shifted}
                  onChange={(e) => updateField("decisions_shifted", e.target.value)}
                  placeholder="List the decisions that have changed..."
                  className="min-h-24"
                  required
                  disabled={isCompleted()}
                  data-testid="s3-shifted"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">
                  2. Where does execution already feel easier?
                </Label>
                <Textarea
                  value={formData.execution_easier}
                  onChange={(e) => updateField("execution_easier", e.target.value)}
                  placeholder="Identify where things are flowing better..."
                  className="min-h-24"
                  required
                  disabled={isCompleted()}
                  data-testid="s3-easier"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">
                  3. Where do you still notice hesitation or friction?
                </Label>
                <Textarea
                  value={formData.remaining_friction}
                  onChange={(e) => updateField("remaining_friction", e.target.value)}
                  placeholder="What's still creating resistance..."
                  className="min-h-24"
                  required
                  disabled={isCompleted()}
                  data-testid="s3-friction"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-base font-medium">
                  4. What must happen in the next 30 days to confirm progress?
                </Label>
                <Textarea
                  value={formData.next_30_days}
                  onChange={(e) => updateField("next_30_days", e.target.value)}
                  placeholder="Define concrete milestones for the next month..."
                  className="min-h-24"
                  required
                  disabled={isCompleted()}
                  data-testid="s3-30days"
                />
              </div>

              {!isCompleted() && (
                <div className="pt-4">
                  <Button 
                    type="submit" 
                    className="w-full bg-orange-600 hover:bg-orange-700 h-12 text-base"
                    disabled={submitting}
                    data-testid="s3-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit & Prepare for Session 3
                      </>
                    )}
                  </Button>
                </div>
              )}

              {enrollment?.session_3?.pre_session_response && !isCompleted() && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-orange-800 font-medium">Pre-session reflection submitted</p>
                    <p className="text-orange-700 text-sm">Your consultant will review your responses and provide feedback during the session.</p>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Section 3: Execution Priorities (Post-Session) */}
        <Card className={`border-neutral-200 ${!isCompleted() ? "opacity-60" : ""}`}>
          <CardHeader className="bg-green-50 border-b border-green-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <div>
                  <CardTitle className="text-lg">Execution Priorities Locked</CardTitle>
                  <CardDescription className="text-green-700 mt-1">
                    Your focused action items moving forward.
                  </CardDescription>
                </div>
              </div>
              {!isCompleted() && <Lock className="w-5 h-5 text-green-400" />}
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {isCompleted() && enrollment?.session_3?.outputs?.execution_priorities ? (
              <div className="space-y-6">
                {enrollment.session_3.outputs.execution_priorities.priority_decisions && (
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-2 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                      Priority Decisions
                    </h4>
                    <p className="text-neutral-700 bg-green-50 p-3 rounded-lg">
                      {enrollment.session_3.outputs.execution_priorities.priority_decisions}
                    </p>
                  </div>
                )}
                {enrollment.session_3.outputs.execution_priorities.immediate_actions && (
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-2 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                      Immediate Actions
                    </h4>
                    <p className="text-neutral-700 bg-green-50 p-3 rounded-lg">
                      {enrollment.session_3.outputs.execution_priorities.immediate_actions}
                    </p>
                  </div>
                )}
                {enrollment.session_3.outputs.execution_priorities.delegations && (
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-2 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                      Delegations or Removals
                    </h4>
                    <p className="text-neutral-700 bg-green-50 p-3 rounded-lg">
                      {enrollment.session_3.outputs.execution_priorities.delegations}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-500">
                <Lightbulb className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                <p>Your consultant will fill this in after your session.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 4: Decision Review Framework (Post-Session) */}
        <Card className={`border-neutral-200 ${!isCompleted() ? "opacity-60" : ""}`}>
          <CardHeader className="bg-teal-50 border-b border-teal-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <RefreshCw className="w-5 h-5 text-teal-600" />
                <div>
                  <CardTitle className="text-lg">Decision Review Framework</CardTitle>
                  <CardDescription className="text-teal-700 mt-1">
                    Use this framework to self-correct without external support.
                  </CardDescription>
                </div>
              </div>
              {!isCompleted() && <Lock className="w-5 h-5 text-teal-400" />}
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {isCompleted() && enrollment?.session_3?.outputs?.review_framework ? (
              <div className="space-y-6">
                {enrollment.session_3.outputs.review_framework.weekly_check_questions && (
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-2">Weekly Decision Check Questions</h4>
                    <div className="bg-teal-50 p-4 rounded-lg">
                      <p className="text-neutral-700 whitespace-pre-wrap">
                        {enrollment.session_3.outputs.review_framework.weekly_check_questions}
                      </p>
                    </div>
                  </div>
                )}
                {enrollment.session_3.outputs.review_framework.red_flags && (
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-2">Red Flags</h4>
                    <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
                      <p className="text-neutral-700">
                        {enrollment.session_3.outputs.review_framework.red_flags}
                      </p>
                    </div>
                  </div>
                )}
                {enrollment.session_3.outputs.review_framework.recalibration_cues && (
                  <div>
                    <h4 className="font-medium text-neutral-900 mb-2">Recalibration Cues</h4>
                    <div className="bg-teal-50 p-4 rounded-lg">
                      <p className="text-neutral-700">
                        {enrollment.session_3.outputs.review_framework.recalibration_cues}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-500">
                <Lightbulb className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                <p>Your consultant will fill this in after your session.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Consultant Recommendations — visible after session complete */}
        {isCompleted() && enrollment?.session_3?.outputs?.consultant_recommendations && (
          <Card className="border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-white">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Award className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="text-lg text-orange-900">Consultant Recommendations</CardTitle>
                  <CardDescription className="text-orange-700">Strategic advice from your consultant based on Session 3</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="bg-white rounded-lg p-4 border border-orange-100 whitespace-pre-wrap text-neutral-800 leading-relaxed">
                {enrollment.session_3.outputs.consultant_recommendations}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => navigate("/rapid/session/2")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Session 2
          </Button>
          {isCompleted() && (
            <Button onClick={() => navigate("/rapid/momentum")} className="bg-emerald-600 hover:bg-emerald-700">
              Continue to Momentum Plan
              <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RapidSession3Page;
